import { Link as RouterLink } from "react-router-dom";
import {
  Link,
  Table,
  TableBody,
  TableCheckBoxTd,
  TableCheckBoxTh,
  TableHeader,
  TableRow,
  Td,
  Th,
} from "@graffer-inc/console-design-kit";
import {
  displayGroupRole,
  displayName,
} from "../../../../application/domain/rbac/rbac";
import { Group } from "../../../../application/domain/group/group";
import { SettingR } from "../../../pages/settings/route";
import { UserEntity } from "../../../../application/domain/user/user-entity";
import { ToggleTag } from "../../../organisms/common/toggle-tag";
import React from "react";

export type TenantUserListProps = {
  users: Array<UserEntity>;
  selectedUsers: Array<UserEntity>;
  setSelectedUsers: (users: Array<UserEntity>) => void;
  groupMaps: { [groupName: string]: Group };
  selectable: boolean;
};

export const TenantUserList = ({
  users,
  groupMaps,
  selectedUsers,
  setSelectedUsers,
  selectable,
}: TenantUserListProps) => {
  const [allChecked, setAllChecked] = React.useState(false);

  return (
    <Table borderType="row">
      <TableHeader>
        <TableRow>
          {selectable && (
            <TableCheckBoxTh
              checked={allChecked}
              onCheckAll={() => {
                setAllChecked(!allChecked);
                if (allChecked) {
                  setSelectedUsers([]);
                  return;
                }
                setSelectedUsers(users);
              }}
            />
          )}
          <Th>アカウント名(氏名)</Th>
          <Th>メールアドレス</Th>
          <Th>ロール</Th>
          <Th>グループ・グループロール</Th>
          <Th>認証方式</Th>
          <Th>2段階認証</Th>
          <Th>ステータス</Th>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => {
          return (
            <TableRow key={user.id}>
              {selectable && (
                <TableCheckBoxTd
                  checked={selectedUsers.includes(user)}
                  onCheck={(e) => {
                    if (e.target.checked) {
                      setSelectedUsers([...selectedUsers, user]);
                    } else {
                      setSelectedUsers(selectedUsers.filter((u) => u !== user));
                    }
                  }}
                />
              )}
              <Td>
                <div className="c-word-break-all">
                  <Link
                    anchor={({ ...props }) => (
                      <RouterLink
                        {...props}
                        to={SettingR.UserDetail.link({ id: user.id })}
                      />
                    )}
                  >
                    {user.accountName}
                    <br />({user.name})
                  </Link>
                </div>
              </Td>
              <Td>{user.email}</Td>
              <Td>
                {user.roles.map((role, i) => (
                  <div className="c-word-break-all" key={`${user.id}-role${i}`}>
                    {displayName(role)}
                  </div>
                ))}
              </Td>
              <Td>
                <GroupAndRoleList user={user} groupMaps={groupMaps} />
              </Td>
              <Td>{user.authMethod === "password" ? "パスワード" : "SAML"}</Td>
              <Td>{user.isTOTPEnabled() ? "設定済み" : "未設定"}</Td>
              <Td>
                <ToggleTag disabled={user.disabled} />
              </Td>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const GroupAndRoleList: React.FC<{
  user: UserEntity;
  groupMaps: { [groupName: string]: Group };
}> = ({ user, groupMaps }) => {
  // 所属するグループが多い場合、ユーザー一覧で全てを表示しても見づらいため、一部分のみを表示する
  const displayableGroupCount = 2;

  return (
    <ul>
      {user.groupIDs.slice(0, displayableGroupCount).map((groupId, index) => {
        const group = groupMaps[groupId];
        {
          /*
          ユーザーは少なくとも1つ以上のグループに所属しているはずであり、
          groupMaps にはすべてのグループが含まれているため、
          ここで null が返ることは想定されない。
          */
        }
        if (!group) return null;

        return (
          <li key={`${group.id}-${index}`}>
            {group.name}&nbsp;:&nbsp;
            {displayGroupRole(user.groupsRoles[group.id!])}
          </li>
        );
      })}
      {user.groupIDs.length > displayableGroupCount && (
        <li>
          ...他{user.groupIDs.length - displayableGroupCount}
          つのグループに所属
        </li>
      )}
    </ul>
  );
};
